import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { CloudArrowUpIcon, ViewColumnsIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import Button from '../../../../ui/canon/Button';
import SearchBar from '../../../../ui/canon/SearchBar';

const VaultHeader = ({
  viewMode,
  onViewModeChange,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  selectedSource,
  onSourceChange,
  sortBy,
  onSortChange,
  onUploadClick,
  documentCount,
  categories = [],
  sources = [],
}) => {
  const intl = useIntl();

  const handleViewModeToggle = () => {
    if (viewMode === 'card') {
      onViewModeChange('grid');
    } else if (viewMode === 'grid') {
      onViewModeChange('list');
    } else {
      onViewModeChange('card');
    }
  };

  const getViewIcon = () => {
    if (viewMode === 'card') {
      return <ListBulletIcon className="h-5 w-5" />;
    } else if (viewMode === 'grid') {
      return <Squares2X2Icon className="h-5 w-5" />;
    } else {
      return <ViewColumnsIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-succession-text-primary mb-1">
            {intl.formatMessage({ id: 'succession.vault.heading' })}
          </h1>
          <p className="text-sm text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.vault.subtitle' }, { count: documentCount })}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={onUploadClick}
          className="ml-4 flex items-center gap-2"
        >
          <CloudArrowUpIcon className="h-4 w-4" />
          {intl.formatMessage({ id: 'succession.vault.upload_document' })}
        </Button>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <SearchBar
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={intl.formatMessage({ id: 'succession.vault.search_placeholder' })}
            variant="outline"
            size="md"
          />
        </div>

        <div className="relative min-w-[160px]">
          <select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full h-9 bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 appearance-none pr-8"
          >
            <option value="all">{intl.formatMessage({ id: 'succession.vault.all_categories' })}</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-4 w-4 text-succession-text-muted" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <div className="relative min-w-[160px]">
          <select
            value={selectedSource}
            onChange={(e) => onSourceChange(e.target.value)}
            className="w-full h-9 bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 appearance-none pr-8"
          >
            <option value="all">{intl.formatMessage({ id: 'succession.vault.all_sources' })}</option>
            {sources.map(src => (
              <option key={src} value={src}>{src.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-4 w-4 text-succession-text-muted" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <div className="relative min-w-[180px]">
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="w-full h-9 bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 appearance-none pr-8"
          >
            <option value="date-desc">{intl.formatMessage({ id: 'succession.vault.sort_date_desc' })}</option>
            <option value="date-asc">{intl.formatMessage({ id: 'succession.vault.sort_date_asc' })}</option>
            <option value="name-asc">{intl.formatMessage({ id: 'succession.vault.sort_name_asc' })}</option>
            <option value="name-desc">{intl.formatMessage({ id: 'succession.vault.sort_name_desc' })}</option>
            <option value="size-desc">{intl.formatMessage({ id: 'succession.vault.sort_size_desc' })}</option>
            <option value="size-asc">{intl.formatMessage({ id: 'succession.vault.sort_size_asc' })}</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-4 w-4 text-succession-text-muted" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <button
          onClick={handleViewModeToggle}
          className="h-9 w-9 flex items-center justify-center text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
          title={intl.formatMessage({ id: 'succession.vault.toggle_view' })}
        >
          {getViewIcon()}
        </button>
      </div>
    </div>
  );
};

VaultHeader.propTypes = {
  viewMode: PropTypes.oneOf(['card', 'grid', 'list']).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  selectedCategory: PropTypes.string.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  selectedSource: PropTypes.string.isRequired,
  onSourceChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  documentCount: PropTypes.number.isRequired,
  categories: PropTypes.array,
  sources: PropTypes.array,
};

export default VaultHeader;
